import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getSampleProspects, getTeamProspectRankings, FARM_PERIODS } from '../../services/prospectFixtures';

/**
 * Farm page — left: one board of every prospect, hitters and pitchers together,
 * with both stat categories (Hitting + Pitching) side by side and a time-period
 * filter (Today / Yesterday / L7 / L30 / YTD / 2025). Right: a slim team farm-
 * system ranking rail. Runs on local sample data.
 */

// Both stat-category column sets. `asc: true` = lower is better (ERA/WHIP), so the
// first click on that header sorts ascending.
const HIT_COLS = [
  { key: 'AVG', label: 'AVG' },
  { key: 'HR', label: 'HR' },
  { key: 'RBI', label: 'RBI' },
  { key: 'R', label: 'R' },
  { key: 'SB', label: 'SB' },
  { key: 'OPS', label: 'OPS' },
];
const PIT_COLS = [
  { key: 'W', label: 'W' },
  { key: 'ERA', label: 'ERA', asc: true },
  { key: 'WHIP', label: 'WHIP', asc: true },
  { key: 'K', label: 'K' },
  { key: 'SV', label: 'SV' },
  { key: 'IP', label: 'IP' },
];
const ALL_POS = ['C', '1B', '2B', '3B', 'SS', 'OF', 'RHP', 'LHP'];
const LEVELS = ['A', 'A+', 'AA', 'AAA'];
const RANK_KEYS = new Set(['overall', 'avgRank', 'age', 'eta']);
const SCOPES = [
  { id: 'mine', label: 'My Prospects' },
  { id: 'available', label: 'Available' },
  { id: 'all', label: 'All' },
];

function LeagueFarm() {
  const { userTeam } = useOutletContext();
  const teamId = userTeam?.id ?? userTeam?.teamId ?? 'team-1';

  const [scope, setScope] = useState('mine');
  const [pos, setPos] = useState('ALL');
  const [level, setLevel] = useState('ALL');
  const [period, setPeriod] = useState('ytd');
  const [sortKey, setSortKey] = useState('overall');
  const [sortDir, setSortDir] = useState('asc');

  const allProspects = getSampleProspects();
  const teamRankings = getTeamProspectRankings();

  const rows = useMemo(() => {
    let list = allProspects;
    if (scope === 'mine') list = list.filter((p) => p.fantasyTeamId === teamId);
    else if (scope === 'available') list = list.filter((p) => !p.fantasyTeamId);
    if (pos !== 'ALL') list = list.filter((p) => p.position === pos);
    if (level !== 'ALL') list = list.filter((p) => p.level === level);
    const num = (p) => {
      if (RANK_KEYS.has(sortKey)) return p[sortKey] ?? 0;
      const v = (p.periodStats?.[period] ?? {})[sortKey];
      const n = typeof v === 'string' ? parseFloat(v) : v;
      return Number.isFinite(n) ? n : null; // blanks (N/A category, did-not-play) sort last
    };
    return [...list].sort((a, b) => {
      const av = num(a);
      const bv = num(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      const d = av - bv;
      return sortDir === 'asc' ? d : -d;
    });
  }, [allProspects, scope, pos, level, period, sortKey, sortDir, teamId]);

  const toggleSort = (key, asc) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(asc ? 'asc' : 'desc');
    }
  };
  const arrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const th = (key, label, { align = 'right', asc = false, cls = '' } = {}) => (
    <th
      key={key}
      className={`${align === 'right' ? 'rz-num' : ''} ${cls}`.trim() || undefined}
      style={{ textAlign: align, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
      onClick={() => toggleSort(key, asc)}
      title="Sort"
    >
      {label}{arrow(key)}
    </th>
  );

  // A stat cell for one category: the value (or '—' for did-not-play) when the
  // category applies to this player, otherwise a muted dot (a hitter under
  // Pitching, or vice versa). `divide` draws the group boundary line.
  const statCell = (p, col, applies, divide) => {
    const cls = `rz-c-stat rz-num${divide ? ' farm-div' : ''}`;
    if (!applies) return <td key={col.key} className={cls}><span className="farm-na">·</span></td>;
    const v = (p.periodStats?.[period] ?? {})[col.key];
    return <td key={col.key} className={cls}>{v ?? '—'}</td>;
  };

  const ownerCol = scope !== 'mine';
  const colCount = 4 + HIT_COLS.length + PIT_COLS.length + 1 + (ownerCol ? 1 : 0);

  return (
    <div className="farm-grid">
      {/* Left: combined prospect stat board — scope, filters, period, sortable columns */}
      <div className="rz-card">
        <div className="rz-card-head" style={{ flexWrap: 'wrap', gap: '10px' }}>
          <div className="rz-seg" role="tablist" aria-label="Prospect scope">
            {SCOPES.map((s) => (
              <button key={s.id} type="button" role="tab" aria-selected={scope === s.id} className={scope === s.id ? 'on' : ''} onClick={() => setScope(s.id)}>{s.label}</button>
            ))}
          </div>
          <span className="rz-count">{rows.length}</span>
        </div>

        {/* Filters: position + level on the left, stat time-period on the right */}
        <div className="farm-period-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div className="rz-seg">
              <select className="rz-subselect" aria-label="Filter by position" value={pos} onChange={(e) => setPos(e.target.value)}>
                <option value="ALL">All Pos</option>
                {ALL_POS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="rz-seg">
              <select className="rz-subselect" aria-label="Filter by level" value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="ALL">All Lvls</option>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span className="farm-period-label">Stats</span>
            <div className="rz-seg farm-period-seg" role="tablist" aria-label="Stat time period">
              {FARM_PERIODS.map((per) => (
                <button key={per.id} type="button" role="tab" aria-selected={period === per.id} className={period === per.id ? 'on' : ''} onClick={() => setPeriod(per.id)}>{per.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="farm-board-wrap">
          <table className="rz-tbl rz-tbl-players farm-board">
            <thead>
              <tr className="farm-grp">
                <th colSpan={4} aria-hidden="true" />
                <th className="farm-grp-hit" colSpan={HIT_COLS.length}>Hitting</th>
                <th className="farm-grp-pit farm-div" colSpan={PIT_COLS.length}>Pitching</th>
                <th colSpan={1 + (ownerCol ? 1 : 0)} aria-hidden="true" />
              </tr>
              <tr className="farm-cols">
                {th('overall', '#', { align: 'left', asc: true })}
                <th className="rz-c-player" style={{ textAlign: 'left' }}>Prospect</th>
                {th('age', 'Age', { asc: true })}
                {th('eta', 'ETA', { asc: true })}
                {HIT_COLS.map((c) => th(c.key, c.label, { asc: c.asc }))}
                {PIT_COLS.map((c, i) => th(c.key, c.label, { asc: c.asc, cls: i === 0 ? 'farm-div' : '' }))}
                {th('avgRank', 'Rk', { asc: true })}
                {ownerCol && <th style={{ textAlign: 'left' }}>Owner</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={colCount} style={{ padding: '16px', color: 'var(--muted)', fontSize: '13px' }}>No prospects match these filters.</td></tr>
              ) : (
                rows.map((p) => {
                  const isP = p.isPitcher;
                  return (
                    <tr key={p.id} className="rz-row rz-filled">
                      <td><span className="farm-rank">#{p.overall}</span></td>
                      <td className="rz-c-player">
                        <div className="rz-pc-main">
                          <div className="rz-pc-name">{p.name}</div>
                          <div className="rz-pc-meta">{p.org} &middot; {p.position} &middot; {p.level}</div>
                        </div>
                      </td>
                      <td className="rz-num">{p.age}</td>
                      <td className="rz-num">{p.eta}</td>
                      {HIT_COLS.map((c) => statCell(p, c, !isP, false))}
                      {PIT_COLS.map((c, i) => statCell(p, c, isP, i === 0))}
                      <td className="rz-c-stat rz-num">{p.avgRank}</td>
                      {ownerCol && (
                        <td style={{ fontSize: '12px', whiteSpace: 'nowrap', color: p.fantasyTeamId === teamId ? 'var(--primary)' : (p.fantasyTeamId ? 'var(--muted)' : 'var(--muted-foreground)'), fontWeight: p.fantasyTeamId === teamId ? 600 : 400 }}>
                          {p.fantasyTeamName || 'FA'}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: slim team farm-system rankings */}
      <div className="rz-card farm-rail">
        <div className="rz-card-head"><h2>Farm Rankings</h2></div>
        <div>
          {teamRankings.map((t) => (
            <div key={t.teamId} className={`farm-rank-row${t.teamId === teamId ? ' mine' : ''}`}>
              <span className="farm-rank">#{t.rank}</span>
              <div className="farm-rank-main">
                <div className="farm-rank-team">{t.teamName}{t.teamId === teamId && <span className="farm-you">You</span>}</div>
                <div className="farm-rank-sub">{t.count} prospects &middot; {t.top30} top-30</div>
              </div>
              <span className="farm-rank-score">{t.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LeagueFarm;
